/* eslint import/no-webpack-loader-syntax: off */
import React, { useState } from "react";

import Form from "react-bootstrap/Form";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";
import Button from "react-bootstrap/Button";

import JSZip from "jszip";
import { saveAs } from "file-saver";
import PDFDocument from "pdfkit/js/pdfkit.standalone";
import SVGtoPDF from "../SVGToPDF";

import versiculumUrl from "../assets/versiculum.ttf";
import greextraUrl from "../assets/greextra.woff";
import garamondPremierUrl from "../assets/GaramondPremrPro.otf";
import garamondPremierBoldUrl from "../assets/GaramondPremrPro-Bd.otf";
import garamondPremierItalicUrl from "../assets/GaramondPremrPro-It.otf";
import garamondPremierBoldItalicUrl from "../assets/GaramondPremrPro-BdIt.otf";
import barrouxFontUrl from "../assets/Barroux-Regular.ttf";
import barrouxFontBoldUrl from "../assets/Barroux-Bold.ttf";
import barrouxFontItalicUrl from "../assets/Barroux-Italic.ttf";
import barrouxFontBoldItalicUrl from "../assets/Barroux-BoldItalic.ttf";
import plantinFontUrl from "../assets/Plantin MT Pro Regular.ttf";
import plantinFontBoldUrl from "../assets/Plantin MT Pro Bold.ttf";
import plantinFontItalicUrl from "../assets/Plantin MT Pro Reg-Italic.ttf";
import plantinFontBoldItalicUrl from "../assets/Plantin MT Pro Bold-Italic.ttf";
import usePersistentState from "../js/usePersistentState";
import { fontKeys } from '../utils/fonts';
import { listGoogleFontsUrl } from '../utils/googleFonts.mjs';

import {
  saveSvg,
  saveSvgAsPng,
  svgAsDataUri,
  svgAsPngUri
} from "save-svg-as-png";


import ChantDocument from "../js/ChantDocument";

import "../scss/ExportPreview.scss";

/**
 * Remove pseudo selectors from SVG CSS
 * @param {string} sel 
 * @param {string} props 
 * @returns 
 */
const modifyCss = (sel, props) => /:/.test(sel) ? '' : `${sel}{${props}}\n`;
/**
 * 
 * @param {PDFKit.PDFDocument} doc 
 * @returns {Promise<Blob>}
 */
const pdfToBlob = (doc) =>
  new Promise((resolve, reject) => {
    const buffers = [];
    doc.on('data', buffers.push.bind(buffers));
    doc.on('end', () => resolve(new Blob(buffers, { type: 'application/pdf' })));
    doc.on('error', reject);
    doc.end();
  });

const removeAttributes = (node, attributes) => {
  for (let key of attributes) {
    node.removeAttribute(key);
  }
  for (let childNode of node.children) {
    removeAttributes(childNode, attributes);
  }
};

const otherFonts = {
  "Garamond Premier Pro": {
    format: "opentype",
    files: {
      regular: garamondPremierUrl,
      italic: garamondPremierItalicUrl,
      "700": garamondPremierBoldUrl,
      "700italic": garamondPremierBoldItalicUrl
    }
  },
  "Barroux": {
    format: "truetype",
    files: {
      regular: barrouxFontUrl,
      italic: barrouxFontItalicUrl,
      "700": barrouxFontBoldUrl,
      "700italic": barrouxFontBoldItalicUrl
    }
  },
  "Plantin MT Pro": {
    format: "truetype",
    files: {
      regular: plantinFontUrl,
      italic: plantinFontItalicUrl,
      "700": plantinFontBoldUrl,
      "700italic": plantinFontBoldItalicUrl,
    }
  },
  "Versiculum": {
    format: "truetype",
    files: {
      regular: versiculumUrl,
    }
  },
  "greextra": {
    format: "woff",
    files: {
      regular: greextraUrl,
    },
  }
};

const fontTypes = {
  regular: { style: "normal", weight: "normal", suffix: "" },
  italic: { style: "italic", weight: "normal", suffix: "-Italic" },
  "700": { style: "normal", weight: "bold", suffix: "-Bold" },
  "700italic": { style: "italic", weight: "bold", suffix: "-BoldItalic" }
};

const makeFontCss = (family, url, style, format = "truetype") => `@font-face {
  font-family: "${family}";
  font-style: ${style.style};
  font-weight: ${style.weight};
  src: url(${url}) format("${format}");
}`;

/**
 * 
 * @param {{ document: ChantDocument}} param
 * @returns 
 */
export default function ExportPreview({ document: doc }) {
  const [fileType, setFileType] = useState("pdf");
  const [cropped, setCropped] = usePersistentState('croppedExport', false);

  const startExport = async () => {
    if (fileType === 'gabc') {
      return doGabcExport();
    }
    // first, get the font URLs using Google Fonts Developer API:
    let response = null;
    const fonts = [];
    try {
      response = await fetch(listGoogleFontsUrl);
    } catch (exception) {
      // failed to download font info
    }
    const data = await response?.json();
    let fontsByFamily = {
      ...otherFonts,
      ...(data ? Object.fromEntries(data.items.map(font => [font.family, font])) : {})
    };
    // TODO: only include Versiculum and greextra if they are used within the GABC
    let fontsToLoad = ['Versiculum', 'greextra'];
    fontKeys.forEach(fontKey => {
      let fontFamily = doc[fontKey] || ChantDocument.defaultFont
      if (
        fontFamily &&
        fontFamily in fontsByFamily &&
        !fontsToLoad.includes(fontFamily)
      ) {
        fontsToLoad.push(fontFamily);
      }
    });
    for (let fontFamily of fontsToLoad) {
      let font = fontsByFamily[fontFamily];
      let { files, format: fontFormat = "truetype" } = font;
      for (let [fontType, fontStyle] of Object.entries(fontTypes)) {
        if (fontType in files) {
          fonts.push({
            text: makeFontCss(
              fontFamily,
              files[fontType],
              fontStyle,
              fontFormat
            ),
            url: files[fontType],
            format: "font/ttf",
            fontFamily,
            familyWithSuffix: fontFamily + fontStyle.suffix
          });
        }
      }
    }
    let resolution = ChantDocument.getPixelMultiplier(doc.units),
      options = {};

    if (fileType === "pdf") {
      let pdfOptions = {};

      resolution = ChantDocument.getPointMultiplier(doc.units);
      let { width, height, margin } = doc;
      let contentWidth = width - 2 * margin,
        contentHeight = height - 2 * margin;
      if (cropped) {
        pdfOptions.margin = 0;
        width = contentWidth;
        height = 0;
        for (
          let svgParent, i = 0;
          (svgParent = document.getElementById("ExsurgeChant-" + i));
          ++i
        ) {
          const svg = svgParent.firstChild;
          height += svg.viewBox.baseVal.height / ChantDocument.getPixelMultiplier(doc.units);
        }
      } else {
        pdfOptions.margin = margin * resolution;
      }
      pdfOptions.size = [width * resolution, height * resolution];
      let pdf = new PDFDocument(pdfOptions);

      options.useCSS = true;
      options.width = contentWidth * resolution;
      options.height = contentHeight * resolution;
      let fontPromises = [],
        fetchOptions = { cache: "force-cache" };
      fontPromises.push(
        fetch(versiculumUrl, fetchOptions).then(response =>
          response.arrayBuffer().then(buffer => {
            pdf.registerFont("Versiculum", buffer);
          })
        ),
        fetch(greextraUrl, fetchOptions).then(response =>
          response.arrayBuffer().then(buffer => {
            pdf.registerFont("greextra", buffer);
          })
        ),
      );
      for (let font of fonts) {
        fontPromises.push(
          fetch(font.url.replace(/^http:/, 'https:'), fetchOptions).then(
            response =>
              response.arrayBuffer().then(buffer => {
                console.info(
                  "Registering",
                  font.familyWithSuffix,
                  font.url,
                  buffer
                );
                pdf.registerFont(font.familyWithSuffix, buffer);
              }),
              error => {
                console.warn(error, font);
              }
          )
        );
      }
      await Promise.all(fontPromises);
      doExport(pdf, null, options, pdfOptions);
    } else {
      options.fonts = fonts;
      let zip = new JSZip();
      if (!cropped) {
        let margin = doc.margin * resolution;
        options = {
          ...options,
          left: -margin,
          top: -margin,
          width: doc.width * resolution,
          height: doc.height * resolution
        };
      }

      if (fileType === "png") {
        options = {
          ...options,
          scale: (doc.dpi && (typeof doc.dpi === 'number') ? doc.dpi : 300) / ChantDocument.getPixelMultiplier('in'),
          backgroundColor: "#fff"
        };
      }
      doExport(null, zip, options);
    }
  };

  const doGabcExport = () => {
    let baseFileName = doc.name || "Untitled Document";
    console.info(baseFileName, doc.gabc);
    saveAs(new Blob([doc.gabc], { type: 'text/plain' }), baseFileName + ".gabc");
  }

  /**
   * 
   * @param {*} pdf 
   * @param {JSZip} zip 
   * @param {*} options 
   * @param {*} pdfOptions 
   */
  const doExport = (pdf, zip, options, pdfOptions) => {
    const svgOptions = {
      excludeUnusedCss: true,
      modifyCss,
    }
    let baseFileName = doc.name || "Untitled Document";
    let removeURIPrefix = uri => uri.replace(/^data:[^;]*;base64,/, "");
    let svg,
      i = 0;
    for (
      let svgParent;
      (svgParent = document.getElementById("ExsurgeChant-" + i));
      ++i
    ) {
      svg = svgParent.firstChild;
      const originalSvg = svg;
      if (fileType === "pdf") {
        originalSvg.classList.remove('EditableChantScore');
      } else {
        // get rid of some of the attributes we don't need:
        svg = svg.cloneNode(true);
        // we remove certain attributes to save space in the SVG, but also because Chrome
        // gives the wrong innerHTML for attributes that contain tags, such as source-gabc when
        // a lyric or title has formatting tags (e.g., <b>, etc.)
        removeAttributes(svg, [
          "source-index",
          "element-index",
          "source-gabc",
          "source"
        ]);
        svg.classList.remove('EditableChantScore');
        const insertionCursor = svg.querySelector('g.InsertionCursor');
        insertionCursor?.remove();
      }
      let fileName = `${baseFileName} - Page ${i + 1}.${fileType}`,
        promise;
      switch (fileType) {
        case "pdf":
          if (i > 0) pdf.addPage(pdfOptions);
          let svgPdfOptions = { ...options, preserveAspectRatio: "true" };
          if (svg.viewBox && svg.viewBox.baseVal) {
            svgPdfOptions.width = (svg.viewBox.baseVal.width * 72) / 96;
            svgPdfOptions.height = (svg.viewBox.baseVal.height * 72) / 96;
          }
          SVGtoPDF(
            pdf,
            svg,
            pdfOptions.margin,
            pdfOptions.margin,
            svgPdfOptions
          );
          originalSvg.classList.add('EditableChantScore');
          break;
        case "svg":
          promise = svgAsDataUri(svg, { ...options, ...svgOptions });
          break;
        case "png":
          promise = svgAsPngUri(svg, options);
          break;
        default:
      }
      if (promise) {
        zip.file(fileName, promise.then(removeURIPrefix), { base64: true });
      }
    }
    if (i === 1 || fileType === "pdf") {
      // don't bother with the zip if there was only one file
      switch (fileType) {
        case "pdf":
          pdfToBlob(pdf).then(blob => saveAs(blob, baseFileName + ".pdf"));
          break;
        case "svg":
          saveSvg(svg, baseFileName, { ...options, ...svgOptions });
          break;
        case "png":
          saveSvgAsPng(svg, baseFileName, options);
          break;
        default:
      }
    } else {
      zip
        .generateAsync({ type: "blob" })
        .then(blob => saveAs(blob, baseFileName + ".zip"));
    }
  };

  return (
    <div className="ExportPreview d-flex flex-column">
      {/* <div className="ExportPreview-Window p-2">
        <Exsurge
          // style={{
          //   height: doc.height * zoom + "in",
          //   width: doc.width * zoom + "in"
          // }}
          pageNum={0}
          zoom="fit"
          alignment={doc.alignment}
          gabc={doc.gabc}
          useDropCap={doc.dropCap}
          font={doc.font}
          {...ChantDocument.getCalculatedProps(doc)}
        />
      </div> */}
      <div className="d-flex flex-column mb-1">
        <div className="d-flex flex-column mb-3">
          <label className="mb-1">File Type</label>
          <ToggleButtonGroup
            name="fileType"
            value={fileType}
            onChange={setFileType}
            size="sm"
          >
            {["pdf", "png", "svg", "gabc"].map(type => (
              <ToggleButton key={type} variant="outline-primary" value={type}>
                {type.toUpperCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        <div className="d-flex">
          <div className="d-flex flex-column mr-auto">
            <label className="mb-1">Cropped</label>
            <Form.Switch
              id="switchCrop"
              checked={fileType !== 'gabc' && cropped}
              label={(fileType !== 'gabc' && cropped) ? "On" : "Off"}
              onChange={e => setCropped(e.target.checked)}
              disabled={fileType === 'gabc'}
            />
          </div>
          <div className="d-flex">
            <Button className="ml-auto" onClick={startExport}>
              EXPORT
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
